import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { Flags } from '@anyroad/flags';
import BookingRequestActionCreator from 'action_creators/booking_request';
import Dimmer from 'views/shared/ui/dimmer';
import AffiliatePaymentSelector from 'views/anyguide/affiliates/components/AffiliatePaymentSelector';
import ModalActionCreator from 'action_creators/modal';
import { sendEventToSegment } from 'views/anyguide/helpers/analytics/segmentHelpers';
import { getSessionId } from 'views/plugins/tours/page/booking_details/Helpers';
import { Theme } from 'ui_system/theme';
import { CheckoutError } from 'views/plugins/global_error/CheckoutError';
import { useCheckout } from './CheckoutContext';
import { paymentDefaultValuesFromPageOne } from './Helpers';
import { createPageTwoValidationSchema } from './pageTwoValidation';
import type { PaymentFormData, CheckoutPageTwoProps } from './types';

import { LanguageHeader, LanguageSelector } from '../default/header';
import { BookingTimer } from './booking_timer';
import { PartnerHeader } from './header';
import { Summary } from './summary';
import { Payment, type PaymentHandle } from './payment';
import { Discount } from './discount';
import { PoweredBy } from './powered-by';
import { PaymentHeader } from './PaymentHeader';
import { MessageGuide } from './message_guide';

import {
  PageContainer,
  SummaryWrapper,
  ColumnContainer,
  ComponentContainer,
  FullBleed,
  ConfirmationContainer,
  ButtonContainer,
  SubmitButton,
  PaymentSubtext,
  ConfirmationSubtext,
} from './styles';

export const CheckoutPageTwo = ({ onSubmit }: CheckoutPageTwoProps) => {
  const { formatMessage } = useIntl();
  const context = useCheckout();
  const {
    flags,
    summaryProps,
    pricingData,
    tourId,
    bookingRequestId,
    includesTransport,
    isBookingInvoice,
    isLiveBooking,
    isAffiliate,
    isUnpaidBooking,
    isPayLaterEnabled,
    isOutsidePayLaterCutoff,
    partnerInfo,
    willExpireOn,
    i18nList,
    i18nLang,
    zeroPricedTour,
    fullyDiscounted,
    paidInCash,
    total,
    currency,
    stripeTheme,
    tourSettings,
    discountApplied,
    discountLoading,
    discountErrors,
    loading,
    segmentTrackedPages,
    markSegmentTrackedPage,
    isMobileFirstCheckoutWebhooksEnabled,
    webhookError,
    pageOneData,
  } = context;

  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (webhookError) {
      setPaymentLoading(false);
    }
  }, [webhookError]);

  useEffect(() => {
    if (!webhookError) return;

    const defaultTitle = formatMessage({
      id: 'OOPS_SOMETHING_WENT_WRONG',
      defaultMessage: 'Oops, something went wrong.',
    });

    const defaultMessage = formatMessage({
      id: 'AN_ERROR_OCCURRED_WHILE_PROCESSING_YOUR_CARD._PLEASE_TRY_AGAIN.',
      defaultMessage:
        'An error occurred while processing your card. Please try again.',
    });

    ModalActionCreator.show({
      content: (
        <CheckoutError
          title={defaultTitle}
          message={webhookError.message || defaultMessage}
          showButton={false}
        />
      ),
      className: 'small GlobalError-Modal-Plugin modal',
      settings: { closable: true },
      showIcon: true,
    });
  }, [webhookError, formatMessage]);

  useEffect(() => {
    if (!bookingRequestId) return;
    if (segmentTrackedPages.pageTwo) return;
    if (!tourId) return;
    if (!flags?.[Flags['event-tracking-checkout-02112026-enabled']]) return;

    const guideId = summaryProps?.request?.guide_id;
    const guideName = summaryProps?.request?.guide_full_name;
    const device = Theme.mobile.isMobile ? 'mobile' : 'web';

    try {
      sendEventToSegment('Arrive on page', {
        path: window.location.hash || window.location.pathname,
        referrer: document.referrer,
        session_id: getSessionId(),
        page_name: 'Checkout Payment',
        url: window.location.href,
        checkout_flow: 'mobile_first',
        experience_id: tourId,
        account_id: guideId,
        account_name: guideName,
        device,
      });
      markSegmentTrackedPage(2);
    } catch (error) {
      console.log('segment tracking error', error);
    }
  }, [
    bookingRequestId,
    flags,
    markSegmentTrackedPage,
    segmentTrackedPages.pageTwo,
    summaryProps,
    tourId,
  ]);

  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(
    isPayLaterEnabled && isOutsidePayLaterCutoff ? 'pay-later' : 'charge-card'
  );
  const [isPayLaterSelected, setIsPayLaterSelected] = useState<boolean>(
    isPayLaterEnabled && isOutsidePayLaterCutoff
  );
  const [paymentIntentId, setPaymentIntentId] = useState<string | undefined>();
  const billingDetailsRef = useRef<{
    country?: string;
    postal_code?: string;
  }>({});

  const paymentRef = useRef<PaymentHandle>(null);

  const shouldShowPayment = useMemo(() => {
    return zeroPricedTour !== null && !zeroPricedTour;
  }, [zeroPricedTour]);

  const shouldDisablePayment = useMemo(() => {
    return paidInCash;
  }, [paidInCash]);

  const shouldShowPayLater = useMemo(() => {
    return (
      zeroPricedTour !== null &&
      !zeroPricedTour &&
      isPayLaterEnabled &&
      !isUnpaidBooking
    );
  }, [zeroPricedTour, isPayLaterEnabled, isUnpaidBooking]);

  const shouldDisableDiscount = useMemo(() => {
    return !shouldShowPayment || paidInCash || isPayLaterSelected;
  }, [shouldShowPayment, paidInCash, isPayLaterSelected]);

  const validationSchema = useMemo(
    () =>
      createPageTwoValidationSchema(formatMessage, {
        shouldValidatePrivacyPolicy: false,
      }),
    [formatMessage]
  );

  const paymentPrefillFromPageOne = useMemo(
    () => paymentDefaultValuesFromPageOne(pageOneData ?? undefined),
    [pageOneData]
  );

  const formDefaultValues = useMemo(
    () => ({
      message: '',
      is_pay_later_selected: isPayLaterSelected,
      ...paymentPrefillFromPageOne,
    }),
    [isPayLaterSelected, paymentPrefillFromPageOne]
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
  } = useForm<PaymentFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: formDefaultValues,
    mode: 'onBlur',
  });

  const isFormDisabled = loading || isSubmitting || paymentLoading;
  const paymentFormData = watch();

  const handleFormSubmit = useCallback(
    async (data: PaymentFormData) => {
      const submitData: PaymentFormData = {
        ...data,
        billing_country: billingDetailsRef.current.country,
        billing_zip_code: billingDetailsRef.current.postal_code,
      };

      if (
        shouldShowPayment &&
        !shouldDisablePayment &&
        !isPayLaterSelected &&
        paymentRef.current
      ) {
        setPaymentLoading(true);
        paymentRef.current.submit();
      } else {
        onSubmit(submitData);
      }
    },
    [onSubmit, shouldShowPayment, shouldDisablePayment, isPayLaterSelected]
  );

  const handlePaymentTypeChange = useCallback(
    (paymentType: string) => {
      const isPayLater = paymentType === 'pay-later';
      setSelectedPaymentType(paymentType);
      setIsPayLaterSelected(isPayLater);
      setValue('is_pay_later_selected', isPayLater);
    },
    [setValue]
  );

  const handleDiscountApply = useCallback((params: any) => {
    BookingRequestActionCreator.applyDiscount(params);
  }, []);

  const handleDiscountRemove = useCallback((params: any) => {
    BookingRequestActionCreator.removeDiscount(params);
  }, []);

  const renderPartnerHeader = () => {
    const affiliatePayLaterCheckout = isAffiliate && isUnpaidBooking;
    if ((!isBookingInvoice && !isLiveBooking) || affiliatePayLaterCheckout) {
      return null;
    }

    return (
      <PartnerHeader
        isLiveHeader={isLiveBooking}
        hideOnMobile
        bgColor={partnerInfo.brand_color}
        title={partnerInfo.business_name}
        logo={partnerInfo.logo}
      />
    );
  };

  const summaryPropsFormatted = useMemo(
    () => ({
      request: summaryProps.request as any,
      tour_id: tourId?.toString() || '',
      location: summaryProps.location || { label: '', value: '' },
      pricingData,
    }),
    [summaryProps, tourId, pricingData]
  );

  const renderPaymentSubtext = () => {
    if (isPayLaterSelected) {
      return null;
    }

    if (summaryProps.request.payment_subtext) {
      return (
        <PaymentSubtext
          className="contains-brand-link payment-subtext form-details"
          dangerouslySetInnerHTML={{
            __html: summaryProps.request.payment_subtext,
          }}
        />
      );
    }
    return null;
  };

  const renderSubmitButtonLabel = () => {
    const settings = tourSettings;
    const isInvoice = summaryProps.request.is_invoice;

    if (isInvoice) {
      return (
        settings?.submit_invoice_label?.value ||
        formatMessage({ id: 'SUBMIT', defaultMessage: 'Submit' })
      );
    }

    if (isLiveBooking) {
      return formatMessage({ id: 'REGISTER', defaultMessage: 'Register' });
    }

    const label = settings?.submit_booking_label?.value;
    return label === 'Confirm Purchase'
      ? formatMessage({
          id: 'CONFIRM_PURCHASE',
          defaultMessage: 'Confirm Purchase',
        })
      : label ||
          formatMessage({
            id: 'CONFIRM_PURCHASE',
            defaultMessage: 'Confirm Purchase',
          });
  };

  const checkoutPageCssClasses = classnames(
    'CheckoutPage CheckoutMobileFirstPage',
    {
      'is-live': isLiveBooking,
    }
  );

  const confirmationCssClasses = classnames('ConfirmationContainer', {
    'is-live': isLiveBooking,
  });

  const handlePageTwoSubmit = useCallback(() => {
    onSubmit({
      ...paymentFormData,
      payment_intent_id: paymentIntentId,
      billing_country: billingDetailsRef.current.country,
      billing_zip_code: billingDetailsRef.current.postal_code,
    });
  }, [onSubmit, paymentFormData, paymentIntentId]);

  const showBookingTimer = !isBookingInvoice && !isLiveBooking;
  const partnerHeader = renderPartnerHeader();
  return (
    <PageContainer className={checkoutPageCssClasses}>
      <Dimmer fixed transparent loader active={loading} />
      {partnerHeader && <FullBleed>{partnerHeader}</FullBleed>}
      {isLiveBooking && i18nList.length > 0 && (
        <FullBleed>
          <LanguageHeader>
            <LanguageSelector
              value={{ value: i18nLang, label: i18nLang }}
              options={i18nList.map((lang) => ({ value: lang, label: lang }))}
              onChange={() => {}}
            />
          </LanguageHeader>
        </FullBleed>
      )}

      <ColumnContainer className="MobileFirstColumnContainer">
        {!isLiveBooking && (
          <SummaryWrapper>
            <ComponentContainer className="ComponentContainer MobileFirstSummaryColumn">
              <Summary {...summaryPropsFormatted} />
            </ComponentContainer>
          </SummaryWrapper>
        )}

        <ComponentContainer className="ComponentContainer MobileFirstColumn">
          {showBookingTimer && <PaymentHeader />}
          {showBookingTimer && willExpireOn && (
            <BookingTimer
              sessionExpired={willExpireOn}
              tourId={tourId}
              guideId={summaryProps.request.guide_id}
              originUrl={summaryProps.request.origin_url}
            />
          )}

          {shouldShowPayment && !shouldDisablePayment && (
            <div>
              {!shouldDisableDiscount && (
                <Discount
                  additionalParams={{
                    tour_id: tourId,
                    id: bookingRequestId,
                    payment_intent_id: paymentIntentId,
                  }}
                  discountApplied={discountApplied}
                  errors={discountErrors}
                  discountLoading={discountLoading}
                  onApplyDiscount={handleDiscountApply}
                  onRemoveDiscount={handleDiscountRemove}
                />
              )}

              {!fullyDiscounted && (
                <div>
                  {shouldShowPayLater && (
                    <AffiliatePaymentSelector
                      paymentType={selectedPaymentType}
                      paymentTypeChanged={handlePaymentTypeChange}
                      disabledOptions={
                        !isOutsidePayLaterCutoff ? ['pay-later'] : []
                      }
                    />
                  )}
                  {renderPaymentSubtext()}
                  {!isPayLaterSelected && (
                    <Payment
                      ref={paymentRef}
                      bookingRequestId={bookingRequestId}
                      totalPriceCents={Math.round(total * 100)}
                      currency={currency}
                      stripeTheme={stripeTheme}
                      isMobileFirstCheckoutWebhooksEnabled={
                        isMobileFirstCheckoutWebhooksEnabled
                      }
                      handlePageTwoSubmit={handlePageTwoSubmit}
                      defaultValues={paymentPrefillFromPageOne}
                      onIntentCreated={(intentId) => {
                        setPaymentIntentId(intentId);
                      }}
                      onError={() => setPaymentLoading(false)}
                      onBillingDetailsChange={(billingDetails) => {
                        billingDetailsRef.current = {
                          country: billingDetails.country,
                          postal_code: billingDetails.postal_code,
                        };
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </ComponentContainer>
      </ColumnContainer>
      <ConfirmationContainer className={confirmationCssClasses}>
        <MessageGuide
          guideName={summaryProps.request.guide_full_name || ''}
          showHint={includesTransport}
          onChange={(_fieldName: string, value: string) => {
            setValue('message', value);
          }}
        />
        <ButtonContainer>
          <SubmitButton
            type="submit"
            color="primary"
            onClick={handleSubmit(handleFormSubmit)}
            className={classnames({
              loading: isFormDisabled,
              'brand-bg': true,
            })}
            disabled={isFormDisabled}
            data-testid="checkout-page-two-submit"
          >
            {renderSubmitButtonLabel()}
          </SubmitButton>
        </ButtonContainer>
        <ConfirmationSubtext className="hint confirmation-subtext">
          {summaryProps.request.confirmation_subtext}
        </ConfirmationSubtext>
        {!isFormDisabled && (
          <PoweredBy hidePolicyLinks={context.hidePolicyLinks} />
        )}
      </ConfirmationContainer>
    </PageContainer>
  );
};
